import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatIdDate, formatPlate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import StatusPill from "@/components/StatusPill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

export default function SPBEdit() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [spb, setSpb] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slotOptions, setSlotOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [plants, setPlants] = useState([]);
  const [districts, setDistricts] = useState([]);
  const canEditProduk = user?.role === "admin" || user?.role === "admin_kantor";
  const canEditMaster = canEditProduk; // same role guard for plant & distrik
  const [form, setForm] = useState({
    no_polisi: "",
    nama_supir: "",
    jumlah: "",
    nama_barang: "",
    no_so: "",
    slot_antrian: "",
    nama_pelanggan: "",
    alamat_pengiriman: "",
    produk_id: "",
    plant_id: "",
    distrik_id: "",
    reason: "",
    keterangan: "",
  });

  useEffect(() => {
    api.get(`/spb/${id}`)
      .then(({ data }) => {
        setSpb(data);
        setForm({
          no_polisi: data.no_polisi || "",
          nama_supir: data.nama_supir || "",
          jumlah: data.jumlah ?? "",
          nama_barang: data.nama_barang || "",
          no_so: data.no_so || "",
          slot_antrian: data.slot_antrian || "",
          nama_pelanggan: data.nama_pelanggan || "",
          alamat_pengiriman: data.alamat_pengiriman || "",
          produk_id: data.produk_id || "",
          plant_id: data.plant_id || "",
          distrik_id: data.distrik_id || "",
          reason: "",
          keterangan: data.keterangan || "",
        });
      })
      .catch((err) => toast.error(formatApiError(err)));
    api.get("/spb/slot-options").then(({ data }) => setSlotOptions(data.slots || [])).catch(() => {});
    // Master lists — only needed when the current user can swap them.
    if (canEditMaster) {
      api.get("/products").then(({ data }) => setProducts(data || [])).catch(() => {});
      api.get("/plants").then(({ data }) => setPlants(data || [])).catch(() => {});
      api.get("/districts").then(({ data }) => setDistricts(data || [])).catch(() => {});
    }
    // Bootstrap when id changes. Setters + module-scope api are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canEditMaster]);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.no_polisi.trim()) {
      toast.error("No polisi wajib diisi");
      return;
    }
    if (!form.nama_pelanggan.trim() || !form.alamat_pengiriman.trim()) {
      toast.error("Nama pelanggan & alamat pengiriman wajib diisi");
      return;
    }
    if (form.jumlah === "" || isNaN(parseFloat(form.jumlah))) {
      toast.error("Jumlah harus berupa angka");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        no_polisi: form.no_polisi,
        nama_supir: form.nama_supir,
        jumlah: parseFloat(form.jumlah),
        nama_barang: form.nama_barang,
        no_so: form.no_so,
        slot_antrian: form.slot_antrian,
        nama_pelanggan: form.nama_pelanggan.trim(),
        alamat_pengiriman: form.alamat_pengiriman.trim(),
        keterangan: form.keterangan,
      };
      // Only send master-swap ids when the current user is allowed AND
      // the value actually changed — keeps petugas edits from being 403'd.
      if (canEditMaster) {
        if (form.produk_id && form.produk_id !== spb.produk_id) payload.produk_id = form.produk_id;
        if (form.plant_id && form.plant_id !== spb.plant_id)     payload.plant_id = form.plant_id;
        if (form.distrik_id && form.distrik_id !== spb.distrik_id) payload.distrik_id = form.distrik_id;
        const swapReason = (form.reason || "").trim();
        if (swapReason) payload.reason = swapReason;
      }
      await api.patch(`/spb/${id}`, payload);
      toast.success("SPB berhasil diupdate");
      navigate(`/spb/${id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!spb) return <div className="p-8 overline">Memuat...</div>;

  const notEditable = spb.status !== "belum_termuat";

  return (
    <div className="p-6 md:p-8 max-w-3xl">
      <Link
        to={`/spb/${id}`}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
        data-testid="back-to-detail-link"
      >
        <ArrowLeft size={14} /> Kembali ke Detail SPB
      </Link>

      <div className="border-b border-border pb-6 mb-6">
        <div className="overline">Edit SPB</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 font-mono">
          SPB · {spb.no_spb}
        </h1>
        <div className="mt-3"><StatusPill status={spb.status} /></div>
      </div>

      {notEditable && (
        <div
          className="border border-red-500/50 bg-red-500/10 p-4 mb-6 text-sm text-red-500"
          data-testid="edit-locked-notice"
        >
          SPB ini tidak dapat diedit karena status sudah <strong>{spb.status}</strong>.
          Hanya SPB dengan status <em>&quot;belum_termuat&quot;</em> yang boleh diedit.
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6" data-testid="spb-edit-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReadOnlyRow label="Tanggal" value={formatIdDate(spb.tanggal)} />
          {canEditMaster ? (
            <div>
              <Label className="overline block mb-2">Distrik</Label>
              <Select
                value={form.distrik_id || spb.distrik_id || ""}
                onValueChange={(v) => setF({ distrik_id: v })}
                disabled={notEditable || saving || !!(spb.so_allocations && spb.so_allocations.length)}
              >
                <SelectTrigger className="rounded-lg" data-testid="edit-distrik-select">
                  <SelectValue placeholder="Pilih distrik" />
                </SelectTrigger>
                <SelectContent>
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.kode} · {d.nama}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {spb.so_allocations && spb.so_allocations.length > 0 && (
                <p className="text-[11px] text-amber-700 mt-1">
                  ⚠ Distrik terkunci — SPB pakai alokasi SO. Batalkan lalu buat ulang untuk ganti distrik.
                </p>
              )}
            </div>
          ) : (
            <ReadOnlyRow label="Distrik" value={`${spb.distrik_kode} (${spb.distrik_nama})`} />
          )}
          {canEditMaster ? (
            <div>
              <Label className="overline block mb-2">Plant</Label>
              <Select
                value={form.plant_id || spb.plant_id || ""}
                onValueChange={(v) => setF({ plant_id: v })}
                disabled={notEditable || saving || !!(spb.so_allocations && spb.so_allocations.length)}
              >
                <SelectTrigger className="rounded-lg" data-testid="edit-plant-select">
                  <SelectValue placeholder="Pilih plant" />
                </SelectTrigger>
                <SelectContent>
                  {plants.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.kode} · {p.nama}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {spb.so_allocations && spb.so_allocations.length > 0 && (
                <p className="text-[11px] text-amber-700 mt-1">
                  ⚠ Plant terkunci — SPB pakai alokasi SO. Batalkan lalu buat ulang untuk ganti plant.
                </p>
              )}
            </div>
          ) : (
            <ReadOnlyRow label="Plant" value={spb.plant || "-"} />
          )}
          {canEditProduk ? (
            <div>
              <Label className="overline block mb-2">Produk</Label>
              <Select
                value={form.produk_id || spb.produk_id || ""}
                onValueChange={(v) => setF({ produk_id: v })}
                disabled={notEditable || saving || !!(spb.so_allocations && spb.so_allocations.length)}
              >
                <SelectTrigger className="rounded-lg" data-testid="edit-produk-select">
                  <SelectValue placeholder="Pilih produk" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.kode} · {p.nama}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {spb.so_allocations && spb.so_allocations.length > 0 && (
                <p className="text-[11px] text-amber-700 mt-1">
                  ⚠ Produk terkunci — SPB ini sudah memakai alokasi SO. Batalkan lalu buat ulang untuk ganti produk.
                </p>
              )}
            </div>
          ) : (
            <ReadOnlyRow label="Produk" value={spb.produk_nama || "-"} />
          )}
          {canEditMaster && (
            <div className="md:col-span-2">
              <Label className="overline block mb-2">Alasan Ganti Master (opsional, dicatat ke audit)</Label>
              <Input
                value={form.reason}
                onChange={(e) => setF({ reason: e.target.value })}
                disabled={notEditable || saving}
                placeholder="cth. Salah input distrik saat buat SPB"
                className="rounded-lg"
                data-testid="edit-master-reason"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="No Polisi *">
            <Input
              value={form.no_polisi}
              onChange={(e) => setF({ no_polisi: formatPlate(e.target.value) })}
              disabled={notEditable || saving}
              data-testid="edit-no-polisi"
              className="rounded-lg font-mono font-bold"
              placeholder="BK1234AB"
            />
          </Field>
          <Field label="Nama Supir">
            <Input
              value={form.nama_supir}
              onChange={(e) => setF({ nama_supir: e.target.value })}
              disabled={notEditable || saving}
              data-testid="edit-nama-supir"
              className="rounded-lg"
            />
          </Field>
          <Field label="No SO">
            <Input
              value={form.no_so}
              onChange={(e) => setF({ no_so: e.target.value })}
              disabled={notEditable || saving}
              data-testid="edit-no-so"
              className="rounded-lg"
            />
          </Field>
          <Field label={`Jumlah (${spb.satuan || "TON"}) *`}>
            <Input
              type="number"
              step="0.01"
              value={form.jumlah}
              onChange={(e) => setF({ jumlah: e.target.value })}
              disabled={notEditable || saving}
              data-testid="edit-jumlah"
              className="rounded-lg"
            />
          </Field>
          <Field label="Nama Barang" full>
            <Input
              value={form.nama_barang}
              onChange={(e) => setF({ nama_barang: e.target.value })}
              disabled={notEditable || saving}
              data-testid="edit-nama-barang"
              className="rounded-lg"
            />
          </Field>
          <Field label="Slot Antrian" full>
            <Select
              value={form.slot_antrian || "__empty__"}
              onValueChange={(v) => setF({ slot_antrian: v === "__empty__" ? "" : v })}
              disabled={notEditable || saving}
            >
              <SelectTrigger className="rounded-lg font-mono" data-testid="edit-slot-antrian">
                <SelectValue placeholder="Pilih jam antrian" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__empty__">— (kosongkan) —</SelectItem>
                {slotOptions.map((s) => (
                  <SelectItem key={s} value={s} className="font-mono">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Nama Pelanggan *" full>
            <Input
              value={form.nama_pelanggan}
              onChange={(e) => setF({ nama_pelanggan: e.target.value })}
              disabled={notEditable || saving}
              data-testid="edit-nama-pelanggan"
              className="rounded-lg"
            />
          </Field>
          <Field label="Alamat Pengiriman *" full>
            <Textarea
              value={form.alamat_pengiriman}
              onChange={(e) => setF({ alamat_pengiriman: e.target.value })}
              disabled={notEditable || saving}
              data-testid="edit-alamat-pengiriman"
              className="rounded-lg min-h-[80px]"
            />
          </Field>
          <Field label="Keterangan (opsional)" full>
            <Textarea
              value={form.keterangan}
              onChange={(e) => setF({ keterangan: e.target.value })}
              disabled={notEditable || saving}
              data-testid="edit-keterangan"
              className="rounded-lg min-h-[80px]"
              placeholder="Catatan tambahan untuk SPB ini"
            />
          </Field>
        </div>

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={notEditable || saving}
            className="rounded-lg font-bold uppercase tracking-wider"
            data-testid="save-edit-button"
          >
            <FloppyDisk size={16} className="mr-2" />
            {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/spb/${id}`)}
            className="rounded-lg"
            data-testid="cancel-edit-button"
          >
            Batal
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="overline block mb-2">{label}</Label>
      {children}
    </div>
  );
}

function ReadOnlyRow({ label, value }) {
  return (
    <div>
      <Label className="overline block mb-2">{label}</Label>
      <div className="border border-border bg-muted/30 px-3 py-2 text-sm">{value}</div>
    </div>
  );
}
